import { Fragment, useState } from "react";
import { DownOutlined, ExportOutlined, UpOutlined } from "@ant-design/icons";
import { Row, Col, Tooltip } from "antd";
import clsx from "clsx";
import { StatusSignedIcon } from "@/shared/assets/svg/icons";
import { env } from "@/shared/config";
import { normalizeFilestorePath } from "@entities/document";
import {
  DocumentFileRow,
} from "@entities/journal";
import { useExternalLinkHandler } from "@shared/lib/hooks";
import { ExternalLinkModal } from "@shared/ui";
import { fallbackString, triggerUnifiedResize } from "@shared/utils";
import { DocstoreSections } from "../../../../../../project/project-docstore/model/constants";
import {
  ExecutionAct,
  getApprovalStatusLabel,
} from "../../model/types/execution-data";
import "./section-execution-data.scss";

const EXECUTION_ACT_LINK_TOOLTIP =
  "Перейти в раздел исполнительной документации билдокс";

function ExecutionActExternalLink({ linkToActCard }: { linkToActCard: string }) {
  const actUrl = `${env.OLD_FRONTEND_URL || ""}${linkToActCard}`;
  const {
    isModalOpen,
    pendingUrl,
    handleLinkClick,
    confirmNavigation,
    cancelNavigation,
  } = useExternalLinkHandler();

  return (
    <>
      <Tooltip title={EXECUTION_ACT_LINK_TOOLTIP} placement="top">
        <ExportOutlined
          className="execution-data-item__link-icon"
          onClick={(event) => {
            event.stopPropagation();
            handleLinkClick(actUrl, event, "_blank");
          }}
        />
      </Tooltip>
      <ExternalLinkModal
        isOpen={isModalOpen}
        url={pendingUrl}
        onConfirm={confirmNavigation}
        onCancel={cancelNavigation}
      />
    </>
  );
}

export const SectionExecutionData = ({ data }: { data?: ExecutionAct[] }) => {
  const [open, setOpen] = useState<boolean>(true);
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>(
    {},
  );
  const toggleItem = (id: string) => {
    setExpandedItems((prev) => ({ ...prev, [id]: !prev[id] }));
    // Триггерим resize после изменения состояния (подстраховка если transitionend не сработает)
    setTimeout(() => {
      triggerUnifiedResize();
    }, 350);
  };

  const getDocumentTypeLabel = (type: string) => {
    // Используем DocstoreSections для маппинга типов документов
    return (
      DocstoreSections[type as keyof typeof DocstoreSections] || "Документ"
    );
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("ru-RU", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const executionData = data || [];

  return (
    <div className="section-execution-data">
      <div
        className="section-execution-data__header"
        onClick={() => {
          setOpen(!open);
          // Триггерим resize после изменения состояния
          setTimeout(() => {
            triggerUnifiedResize();
          }, 350);
        }}
      >
        <div className="section-execution-data__header__icon">
          {open ? <UpOutlined /> : <DownOutlined />}
        </div>
        <div className="section-execution-data__title">
          Данные по исполнительной документации
        </div>
      </div>

      {open && (
        <div className="section-execution-data__list">
          {executionData.length === 0 ? (
            <div className="execution-data-item__empty">
              Нет данных по исполнительной документации
            </div>
          ) : (
            executionData.map((act) => (
              <div className="execution-data-item" key={act.actId}>
                <div
                  className="execution-data-item__header"
                  onClick={() => toggleItem(act.actId)}
                >
                  <div className="execution-data-item__icon">
                    {expandedItems[act.actId] ? (
                      <UpOutlined />
                    ) : (
                      <DownOutlined />
                    )}
                  </div>
                  <div className="execution-data-item__title">{act.name}</div>
                  {act.linkToActCard ? (
                    <ExecutionActExternalLink linkToActCard={act.linkToActCard} />
                  ) : null}
                  {act.approvalStatus === "SIGNED" ? (
                    <StatusSignedIcon className="execution-data-item__check" />
                  ) : null}
                </div>

                <div
                  className={clsx("execution-data-item__body", expandedItems[act.actId] && "expanded")}
                  onTransitionEnd={(e) => {
                    // Триггерим resize когда заканчивается transition
                    if (e.propertyName === 'max-height' || e.propertyName === 'opacity') {
                      triggerUnifiedResize();
                    }
                  }}
                >
                  {expandedItems[act.actId] && (
                    <div className="execution-data-item__content">
                      <Row gutter={[8, 12]} className="execution-data-item__grid">
                        <Col span={8} className="execution-data-item__label">
                          Дата акта
                        </Col>
                        <Col span={16} className="execution-data-item__text">
                          {fallbackString(formatDate(act.actDate))}
                        </Col>

                        <Col span={8} className="execution-data-item__label">
                          Дата обновления
                        </Col>
                        <Col span={16} className="execution-data-item__text">
                          {fallbackString(formatDate(act.lastUpdated))}
                        </Col>

                        <Col span={8} className="execution-data-item__label">
                          Наименование
                        </Col>
                        <Col span={16} className="execution-data-item__text">
                          <span className="execution-data-item__name-row">
                            {fallbackString(act.name)}
                            {act.linkToActCard ? (
                              <ExecutionActExternalLink
                                linkToActCard={act.linkToActCard}
                              />
                            ) : null}
                          </span>
                        </Col>

                        <Col span={8} className="execution-data-item__label">
                          Статус
                        </Col>
                        <Col span={16} className="execution-data-item__text">
                          {fallbackString(
                            getApprovalStatusLabel(act.approvalStatus),
                          )}
                        </Col>

                        {act.works.map((work, workIndex) => (
                          <Fragment key={work.workId || workIndex}>
                            <Col
                              span={8}
                              className="execution-data-item__label"
                            >
                              Наименование работы
                            </Col>
                            <Col
                              span={16}
                              className="execution-data-item__text"
                            >
                              {fallbackString(work?.workName)}
                            </Col>

                            <Col
                              span={8}
                              className="execution-data-item__label"
                            >
                              Дата работ
                            </Col>
                            <Col
                              span={16}
                              className="execution-data-item__text"
                            >
                              {fallbackString(
                                `${formatDate(work.workDateStart)} - ${formatDate(work.workDateEnd)}`,
                              )}
                            </Col>

                            <Col
                              span={8}
                              className="execution-data-item__label"
                            >
                              Местоположение
                            </Col>
                            <Col
                              span={16}
                              className="execution-data-item__text"
                            >
                              {fallbackString(work.place)}
                            </Col>

                            {(() => {
                              // Группируем документы по типу
                              const groupedDocs = work.relatedDocuments.reduce(
                                (acc, doc) => {
                                  const type = doc.documentType;
                                  if (!acc[type]) {
                                    acc[type] = [];
                                  }
                                  acc[type].push(doc);
                                  return acc;
                                },
                                {} as Record<string, typeof work.relatedDocuments>,
                              );

                              return Object.entries(groupedDocs).map(
                                ([docType, docs]) => (
                                  <Fragment key={docType}>
                                    <Col
                                      span={8}
                                      className="execution-data-item__label"
                                    >
                                      {getDocumentTypeLabel(docType)}
                                    </Col>
                                    <Col
                                      span={16}
                                      className="execution-data-item__text"
                                    >
                                      <div className="execution-data-item__docs-wrap">
                                        {docs.map((doc, docIndex) => {
                                          const linkToPdf = doc.linkToPdf
                                            ? normalizeFilestorePath(doc.linkToPdf)
                                            : undefined;

                                          return (
                                            <DocumentFileRow
                                              key={doc.documentId || docIndex}
                                              inline
                                              fileName={doc.name}
                                              documentUrl={linkToPdf}
                                            />
                                          );
                                        })}
                                      </div>
                                    </Col>
                                  </Fragment>
                                ),
                              );
                            })()}
                          </Fragment>
                        ))}
                      </Row>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};
